import React, { useContext } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useKiosk } from '../hooks/useKiosk';
import { HotelContext } from '../context/hotel';
/**
 * Componente que protege las rutas en modo Kiosk
 * Solo permite acceso a rutas específicas cuando isKioskMode es true
 * 
 * Rutas permitidas en Kiosk (con o sin carpeta de hotel):
 * - /checkin/identify o /{hotelCode}/checkin/identify
 * - /checkin/edit-new o /{hotelCode}/checkin/edit-new
 * - /checkin/edit-new/passenger/:id o /{hotelCode}/checkin/edit-new/passenger/:id
 * - /kiosk/welcome-kiosk o /{hotelCode}/kiosk/welcome-kiosk
 */
export const KioskRouteGuard = ({ children }) => {
    const { isKioskMode, isKioskLoggedIn } = useKiosk();
    const location = useLocation();
    const { data: hotel } = useContext(HotelContext);
    // Validar si la ruta actual es permitida
    const isRouteAllowed = () => {
        const pathname = location.pathname;
        
        // Expresión regular para capturar rutas con o sin prefijo de hotel
        // El prefijo de hotel es opcional: [opcional]/{hotelCode}
        
        // Patrón 1: /checkin/identify (sin carpeta de hotel)
        if (pathname === '/checkin/identify') {
            return true;
        }
        
        // Patrón 2: /{hotelCode}/checkin/identify (con carpeta de hotel)
        const identifyWithHotelRegex = /^\/[a-zA-Z0-9-]+\/checkin\/identify$/;
        if (identifyWithHotelRegex.test(pathname)) {
            return true;
        }

        // Patrón 3: /checkin/edit-new (sin carpeta de hotel)
        if (pathname === '/checkin/edit-new') {
            return true;
        }
        
        // Patrón 4: /{hotelCode}/checkin/edit-new (con carpeta de hotel)
        const editNewWithHotelRegex = /^\/[a-zA-Z0-9-]+\/checkin\/edit-new$/;
        if (editNewWithHotelRegex.test(pathname)) {
            return true;
        }

        // Patrón 5: /welcome-kiosk (sin carpeta de hotel)
        if (pathname === '/kiosk/welcome-kiosk') {
            return true;
        }
        
        // Patrón 6: /{hotelCode}/welcome-kiosk (con carpeta de hotel)
        const welcomeKioskWithHotelRegex = /^\/[a-zA-Z0-9-]+\/kiosk\/welcome-kiosk$/;
        if (welcomeKioskWithHotelRegex.test(pathname)) {
            return true;
        }

        // Patrón 7: /checkin/edit-new/passenger/:id (sin carpeta de hotel)
        const passengerRegex = /^\/checkin\/edit-new\/passenger\/[\w-]+$/;
        if (passengerRegex.test(pathname)) {
            return true;
        }
        
        // Patrón 8: /{hotelCode}/checkin/edit-new/passenger/:id (con carpeta de hotel)
        const passengerWithHotelRegex = /^\/[a-zA-Z0-9-]+\/checkin\/edit-new\/passenger\/[\w-]+$/;
        if (passengerWithHotelRegex.test(pathname)) {
            return true;
        }

        // Patrón 9: /kiosk/login (sin carpeta de hotel)
        if (pathname === '/kiosk/login') {
            return true;
        }
        
        // Patrón 10: /{hotelCode}/kiosk/login (con carpeta de hotel)
        const loginWithHotelRegex = /^\/[a-zA-Z0-9-]+\/kiosk\/login$/;
        if (loginWithHotelRegex.test(pathname)) {
            return true;
        }

        return false;
    };

    // Si estamos en modo Kiosk y la ruta no es permitida, redirigir a /kiosk/login
    // Pero permitir que se vea la página de login sin redirección

    const pathname = location.pathname;
    const isLoginRoute = pathname === '/kiosk/login' || (hotel?.code && pathname === `/${hotel.code}/kiosk/login`);
    
    // Lógica de redirección en modo Kiosk:
    // 1. Si estamos en modo kiosk y NO está logueado y NO está en login -> redirige a login
    if (isKioskMode && !isKioskLoggedIn && !isLoginRoute && hotel?.code) {
        return <Navigate to={`/${hotel.code}/kiosk/login`} replace />;
    }
    
    // 2. Si estamos en modo kiosk y ESTÁ logueado pero intenta ir a /kiosk/login -> redirige a identify
    if (isKioskMode && isKioskLoggedIn && isLoginRoute && hotel?.code) {
        return <Navigate to={`/${hotel.code}/checkin/identify`} replace />;
    }
    
    // 3. Si estamos en modo kiosk, está logueado pero intenta acceder a una ruta no permitida
    if (isKioskMode && isKioskLoggedIn && !isRouteAllowed()) {
        return <Navigate to={`/${hotel.code}/checkin/identify`} replace />;
    }

    return children;
};

export default KioskRouteGuard;

